// Admin → Settings. Branding + AI generation defaults (stored in site_settings).
// docs/replan-v2.md (admin console, slice 5). Secrets stay in Cloudflare Secrets
// (only references shown, per CLAUDE.md §20) — this edits non-secret config.

import { Alert, Button, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { getSettings, updateSettings, type Settings } from '~/lib/admin';

export const Route = createFileRoute('/_authed/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['settings'], queryFn: ({ signal }) => getSettings(signal) });
  const [form, setForm] = useState<Settings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (q.data && !form) setForm(q.data);
  }, [q.data, form]);

  const save = useMutation({
    mutationFn: (patch: Partial<Settings>) => updateSettings(patch),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  if (q.isLoading || !form)
    return <div className="mx-auto max-w-3xl text-sm text-ink-500">Loading…</div>;

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => {
    setForm({ ...form, [k]: v });
    setSaved(false);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-semibold text-admin-900">Settings</h1>
      <p className="mt-1 text-sm text-ink-700">Branding + AI generation defaults.</p>

      {saved && (
        <Alert tone="success" className="mt-4">
          Saved.
        </Alert>
      )}

      <form
        className="mt-6 space-y-4 rounded-2xl border border-burgundy-500/10 bg-white p-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form);
        }}
      >
        <Field label="Brand name">
          <Input value={form.brandName} onChange={(e) => set('brandName', e.target.value)} />
        </Field>
        <Field label="Support email">
          <Input value={form.supportEmail} onChange={(e) => set('supportEmail', e.target.value)} />
        </Field>
        <Field label="AI story tone">
          <textarea
            value={form.aiStoryTone}
            onChange={(e) => set('aiStoryTone', e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-xl border border-ink-500/20 bg-white px-3 py-2 text-ink-900"
          />
        </Field>
        <Field label="AI image style">
          <textarea
            value={form.aiImageStyle}
            onChange={(e) => set('aiImageStyle', e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-xl border border-ink-500/20 bg-white px-3 py-2 text-ink-900"
          />
        </Field>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save settings'}
        </Button>
      </form>

      <p className="mt-4 text-xs text-ink-500">
        Secrets (SkipCash, Brevo, AI keys) live in Cloudflare Secrets — not editable here.
      </p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm font-medium text-ink-700">
      {label}
      {children}
    </label>
  );
}
